import React, { useContext, useEffect, useState } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Layout as DashboardLayout } from 'layouts/portal/layout';
import { AuthContext, DEFAULT_SHOPIFY_SETTINGS, SHOPIFY_TITLE_SECTION_SEPARATOR, SHOPIFY_TITLE_TYPE_SEPARATOR, get_shopify_data } from 'components';
import { IntegrationShopify, IntegrationSquare } from "components/Settings";
import { Tracking } from 'components/Tracking';
import { ShopifyIcon } from 'components/Icons';

import { 
  List,
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Checkbox,
  Autocomplete,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';

import {
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';

import { 
  Typography
} from '@mui/material';

const capitalize = (s) => {
  return String(s[0]).toUpperCase() + String(s).slice(1);
}

const ShopifyLink = (props) => {
  const { name, shopify_domain } = props;
  return <ListItem>
    <ListItemText>{name || shopify_domain}</ListItemText>
    <ListItemIcon><a style={{paddingTop: 3}} href={`https://${shopify_domain}`} target="_blank"><ShopifyIcon/></a></ListItemIcon>
    <ListItemIcon><DeleteForeverIcon/></ListItemIcon>
  </ListItem>
}

const CheckListItem = (props) => {
  const { id, title, value, onChange } = props;

  return (
    <ListItem
      key={value}
      disablePadding
      sx={{ p: 0, mt: 0, mb: 0 }}
    >
      <ListItemButton
        onClick={(e) => { 
          if (onChange) {
            onChange(!value)
          }
        }}
        sx={{ p: 0 }}
      >
        <Checkbox
          id={id}
          key={id}
          sx={{ pt: 0, pb: 0 }}
          edge="start"
          checked={value}
        />
        <ListItemText primary={title} />
      </ListItemButton>
    </ListItem>
  )
}

const ShopifyTitleGenerator = (props) => {
  const { title, onChange } = props;
  const [ value, setValue ] = React.useState(title?.split(SHOPIFY_TITLE_SECTION_SEPARATOR).map((e) => { 
    let properties = e.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
    let title = properties[1];
    if (properties[0] === 'prop') {
      title = title.split('_').map(capitalize).join(' ')
    }

    return { 
      value: e, 
      label: title,
    }
  }) || []);
  const title_options = [
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}brand`, label: 'Brand' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}category`, label: 'Category' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}collection`, label: 'Collection' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}department`, label: 'Department' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}order_number`, label: 'Order Number' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_name`, label: 'Style Name' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_number`, label: 'Style Number' },
  ];

  useEffect(() => {
    // console.log('title change')
    setValue(title?.split(SHOPIFY_TITLE_SECTION_SEPARATOR).map((e) => { 
      let properties = e.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
      let title = properties[1];
      if (properties[0] === 'prop') {
        title = title.split('_').map(capitalize).join(' ')
      }
  
      return { 
        value: e, 
        label: title,
      }
    }) || [])
  }, [title]);

  useEffect(() => {
    if (onChange) {
      onChange(value.map(e => e.value).join(SHOPIFY_TITLE_SECTION_SEPARATOR));
    }
  }, [value]);

  return <ListItem sx={{ pl: 0, pr: 0 }}>
    <Autocomplete
      multiple
      fullWidth
      id="title-filled"
      options={title_options}
      getOptionLabel={(option) => option.label}
      value={value}
      onChange={(event, newValue) => {
        if (event?.target?.value) {
          setValue([
            ...value,
            { value: `text${SHOPIFY_TITLE_TYPE_SEPARATOR}${event?.target?.value}`, label: event?.target?.value }
          ]);
        } else {
          setValue(newValue);
        }
      }}
      freeSolo
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          
          return (
            <Chip 
              variant="outlined" 
              color={option.value.indexOf('prop') !== -1 ? 'secondary' : 'primary'}
              label={option.value.indexOf('prop') !== -1 ? option.label : `"${option.label}"`}
              key={`${index}-${option.value}`} 
              {...tagProps} 
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ shrink: true, sx: { color: 'black' } }}
          label="Product title"
          placeholder="Select a property or enter custom value"
        />
      )}
    />
  </ListItem>
}

const ShopifyTagsSelector = (props) => {
  const { tags, onChange } = props;
  const [ value, setValue ] = React.useState(tags?.map((e) => { 
    let properties = e.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
    let title = properties?.[1] || e;
    if (properties[0] === 'prop') {
      title = title.split('_').map(capitalize).join(' ')
    }

    return { 
      value: e, 
      label: title,
    }
  }) || []);
  const title_options = [
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}brand`, label: 'Brand' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}category`, label: 'Category' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}collection`, label: 'Collection' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}department`, label: 'Department' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}order_number`, label: 'Order Number' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_name`, label: 'Style Name' },
    { value: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_number`, label: 'Style Number' },
  ];

  useEffect(() => {
    if (tags.join('') !== value.map(e => e.value).join('')) {
      setValue(tags?.map((e) => { 
        let properties = e.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
        let title = properties?.[1] || e;
        if (properties[0] === 'prop') {
          title = title.split('_').map(capitalize).join(' ')
        }
    
        return { 
          value: e, 
          label: title,
        }
      }) || [])
    }
  }, [tags]);
  
  useEffect(() => {
    if (onChange) {
      onChange(value.map(e => e.value));
    }
  }, [value]);

  return <ListItem sx={{ pl: 0, pr: 0 }}>
    <Autocomplete
      multiple
      fullWidth
      id="tags-filled"
      options={title_options}
      getOptionLabel={(option) => option.label}
      value={value}
      onChange={(event, newValue) => {
        if (event?.target?.value) {
          setValue([
            ...value,
            { value: event?.target?.value, label: event?.target?.value }
          ]);
        } else {
          setValue(newValue);
        }
      }}
      freeSolo
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          
          return (
            <Chip 
              variant="outlined" 
              color={option.value.indexOf('prop') !== -1 ? 'secondary' : 'primary'}
              label={option.value.indexOf('prop') !== -1 ? option.label : `"${option.label}"`}
              key={`${index}-${option.value}`} 
              {...tagProps} 
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ shrink: true, sx: { color: 'black' } }}
          label="Product tags"
          placeholder="Select a property or enter custom value"
        />
      )}
    />
  </ListItem>
}

const ShopifySalesChannelsSelector = (props) => {
  const { setting_value, onChange } = props;
  const [ value, setValue ] = React.useState(setting_value || []);

  const title_options = [
    `Online Store`,
    `Point of Sale`
  ];

  useEffect(() => {
    if (onChange) {
      setValue(setting_value);
    }
  }, [setting_value]);
  
  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value]);

  return <ListItem sx={{ pl: 0, pr: 0 }}>
    <Autocomplete
      multiple
      fullWidth
      id="sales=channels-filled"
      options={title_options}
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue)
      }}
      freeSolo
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          
          return (
            <Chip 
              variant="outlined" 
              color={title_options.indexOf(option) !== -1 ? 'secondary' : 'primary'}
              label={option}
              key={`${index}-${option.value}`} 
              {...tagProps} 
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ shrink: true, sx: { color: 'black' } }}
          label="Sales channels"
          placeholder="Select a channel to publish items to or enter custom value"
        />
      )}
    />
  </ListItem>
}

const ShopifyLocationSelector = (props) => {
  const { setting_value, onChange, locations } = props;
  const [ value, setValue ] = React.useState(setting_value || []);

  useEffect(() => {
    locations.map((e) => {
      let location = {
        label: e?.node?.name,
        value: e?.node?.id?.replace('gid://shopify/Location/', ''),
      }

      if (e?.node?.isPrimary && setting_value == "Use Default Location") {
        setValue(location)
      }

      if (location.id == setting_value) {
        setValue(location)
      }
    })
  }, [locations])

  useEffect(() => {
    if (!value) return 

    if (onChange) {
      setValue(setting_value);
    }
  }, [setting_value]);
  
  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value]);

  return <ListItem sx={{ pl: 0, pr: 0 }}>
    <Autocomplete
      fullWidth
      id="location-options-filled"
      options={locations.map(e => ({
        label: e?.node?.name,
        value: e?.node?.id,
      }))}
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ shrink: true, sx: { color: 'black' } }}
          label="Location"
          placeholder="Select a location to export items to"
        />
      )}
    />
  </ListItem>
}

const ShopifySettings = (props) => {
  const { name, shopify_domain, settings, shopify_store } = props;
  const { updateShopifyIntegration, deleteShopifyIntegration, notify } = useContext(AuthContext);
  const [ deleteOpen, setDeleteOpen ] = useState(false);

  const integration_key = shopify_store

  const [ title, setTitle ] = React.useState(settings?.hasOwnProperty('title') ? settings.title : DEFAULT_SHOPIFY_SETTINGS.title)
  const [ tags, setTags ] = React.useState(settings?.hasOwnProperty('tags') ? settings.tags : DEFAULT_SHOPIFY_SETTINGS.tags)
  const [ salesChannels, setSalesChannels ] = React.useState(settings?.hasOwnProperty('sales_channels') ? settings.sales_channels : DEFAULT_SHOPIFY_SETTINGS.sales_channels)
  const [ styleAsSku, setStyleAsSku ] = React.useState(settings?.hasOwnProperty('styleNumber_as_sku') ? settings.styleNumber_as_sku : DEFAULT_SHOPIFY_SETTINGS.styleNumber_as_sku)
  const [ brandAsVendor, setBrandAsVendor ] = React.useState(settings?.hasOwnProperty('brand_as_vendor') ? settings?.brand_as_vendor : DEFAULT_SHOPIFY_SETTINGS.brand_as_vendor)
  const [ compareAtPrice, setCompareAtPrice ] = React.useState(settings?.hasOwnProperty('set_compare_at_price') ? settings?.set_compare_at_price : DEFAULT_SHOPIFY_SETTINGS.set_compare_at_price)
  const [ trackInventory, setTrackInventory ] = React.useState(settings?.hasOwnProperty('set_track_quantity') ? settings?.set_track_quantity : DEFAULT_SHOPIFY_SETTINGS.set_track_quantity)
  const [ quantity, setQuantity ] = React.useState(settings?.hasOwnProperty('set_quantity') ? settings?.set_quantity : DEFAULT_SHOPIFY_SETTINGS.set_quantity)
  const [ location, setLocation ] = React.useState(settings?.hasOwnProperty('location') ? settings?.location : DEFAULT_SHOPIFY_SETTINGS.location)
  const [ locations, setLocations ] = React.useState([])
  const [ changed, setChanged ] = React.useState(false)

  useEffect(() => {
    get_shopify_data({
      shopify_store: shopify_store,
      query: `{
        locations(first: 100) {
          edges {
            node {
              id
              name
              isPrimary
            }
          }
        }
      }`
    }).then((e) => {
      if (e?.data) {
        setLocations(e?.data?.data?.locations?.edges)
      }
    })
  }, [])

  useEffect(() => {
    if ((
      settings?.title === title &&
      settings?.tags?.join('') === tags?.join('') &&
      settings?.styleNumber_as_sku === styleAsSku &&
      settings?.brand_as_vendor === brandAsVendor &&
      settings?.set_compare_at_price === compareAtPrice &&
      settings?.set_track_quantity === trackInventory &&
      settings?.sales_channels === salesChannels &&
      true
    )) {
      // console.log("settings?.title === title", settings?.title === title)
      // console.log("settings?.tags?.join('') === tags.join('')", settings?.tags?.join('') === tags.join(''))
      // console.log("settings?.styleNumber_as_sku === styleAsSku", settings?.styleNumber_as_sku === styleAsSku)
      // console.log("settings?.brand_as_vendor === brandAsVendor", settings?.brand_as_vendor === brandAsVendor)
    }


    if (
      (
        settings?.title === title &&
        settings?.tags?.join('') === tags?.join('') &&
        settings?.styleNumber_as_sku === styleAsSku &&
        settings?.brand_as_vendor === brandAsVendor &&
        settings?.set_compare_at_price === compareAtPrice &&
        settings?.set_track_quantity === trackInventory &&
        settings?.set_quantity === quantity &&
        settings?.sales_channels === salesChannels &&
        settings?.location === location &&
        true
      ) || (
        !settings &&
        DEFAULT_SHOPIFY_SETTINGS?.title === title &&
        DEFAULT_SHOPIFY_SETTINGS?.tags?.join('') === tags.join('') &&
        DEFAULT_SHOPIFY_SETTINGS?.styleNumber_as_sku === styleAsSku &&
        DEFAULT_SHOPIFY_SETTINGS?.brand_as_vendor === brandAsVendor &&
        DEFAULT_SHOPIFY_SETTINGS?.set_compare_at_price === compareAtPrice &&
        DEFAULT_SHOPIFY_SETTINGS?.set_track_quantity === trackInventory &&
        DEFAULT_SHOPIFY_SETTINGS?.set_quantity === quantity &&
        DEFAULT_SHOPIFY_SETTINGS?.sales_channels === salesChannels &&
        DEFAULT_SHOPIFY_SETTINGS?.location === location &&
        true
      )
    ) {
      setChanged(false)
    } else {
      setChanged(true)
    }
  }, [title, tags, styleAsSku, brandAsVendor, trackInventory, compareAtPrice, quantity, salesChannels, location])

  const handleCancel = () => {
    setTitle(settings?.hasOwnProperty('title') ? settings.title : DEFAULT_SHOPIFY_SETTINGS.title)
    setTags(settings?.hasOwnProperty('tags') ? settings.tags : DEFAULT_SHOPIFY_SETTINGS.tags)
    setStyleAsSku(settings?.hasOwnProperty('styleNumber_as_sku') ? settings.styleNumber_as_sku : DEFAULT_SHOPIFY_SETTINGS.styleNumber_as_sku)
    setBrandAsVendor(settings?.hasOwnProperty('brand_as_vendor') ? settings?.brand_as_vendor : DEFAULT_SHOPIFY_SETTINGS.brand_as_vendor)
    setCompareAtPrice(settings?.hasOwnProperty('set_compare_at_price') ? settings?.set_compare_at_price : DEFAULT_SHOPIFY_SETTINGS.set_compare_at_price)
    setTrackInventory(settings?.hasOwnProperty('set_track_quantity') ? settings?.set_track_quantity : DEFAULT_SHOPIFY_SETTINGS.set_track_quantity)
    setQuantity(settings?.hasOwnProperty('set_quantity') ? settings?.set_quantity : DEFAULT_SHOPIFY_SETTINGS.set_quantity)
    setSalesChannels(settings?.hasOwnProperty('sales_channels') ? settings?.sales_channels : DEFAULT_SHOPIFY_SETTINGS.sales_channels)
    setLocation(settings?.hasOwnProperty('location') ? settings?.location : DEFAULT_SHOPIFY_SETTINGS.location)
  }

  useEffect(() => {
    handleCancel()
  }, [settings])

  const handleSave = () => {
    const settings = {
      title,
      tags,
      location,
      styleNumber_as_sku: styleAsSku,
      brand_as_vendor: brandAsVendor,
      set_compare_at_price: compareAtPrice,
      set_track_quantity: trackInventory,
      set_quantity: quantity,
      sales_channels: salesChannels,
    }

    updateShopifyIntegration(integration_key, settings, () => {
      setChanged(false)
      notify({ message: 'Settings saved' })
    })
  }

  const handleDeleteShopifyStore = () => {
    deleteShopifyIntegration(integration_key, () => {
      setDeleteOpen(false)
    })
  }

  return (
    <Box sx={{ 
      mt: 0,
      mb: 2,
      p: 2,
      border: '1px solid gray',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, }}>
        <Typography sx={{ mt: 1, mb: 1, fontWeight: 400 }} variant="h6" component="div">Store: {name || shopify_domain}</Typography>
        <Button 
          onClick={() => { setDeleteOpen(true) }}
          sx={{ 
            fontWeight: 400, 
            pr: 0, 
            '&:hover': {
              background: 'white',
            }
          }} 
          endIcon={<DeleteForeverIcon/>}
        >{shopify_domain}</Button>
      </Box>
      <ShopifyTitleGenerator onChange={setTitle} title={title} />
      <ShopifyTagsSelector onChange={setTags} tags={tags} />
      <ShopifySalesChannelsSelector onChange={setSalesChannels} setting_value={salesChannels} />
      <ShopifyLocationSelector locations={locations} onChange={setLocation} setting_value={location} />
      <CheckListItem id="style_as_sku" onChange={setStyleAsSku} title={'Set Style Number as SKU'} value={styleAsSku} />
      <CheckListItem id="brand_as_vendor" onChange={setBrandAsVendor} title={'Set Brand as Vendor'} value={brandAsVendor} />
      <CheckListItem id="set_quantity" onChange={setQuantity} title={'Set Quantity'} value={quantity} />
      <CheckListItem id="set_track_quantity" onChange={setTrackInventory} title={'Set Track Quantity'} value={trackInventory} />
      <CheckListItem id="set_compare_at_price" onChange={setCompareAtPrice} title={'Set Compare-at Price'} value={compareAtPrice} />
      <DialogActions sx={{ p: 0 }}>
        <Button disabled={!changed} onClick={handleCancel} color="secondary" variant='outlined'>Cancel</Button>
        <Button disabled={!changed} onClick={handleSave} color="secondary" variant={changed?'contained':'outlined'}>Save</Button>
        {/* <Button disabled={!changed} onClick={handleCancel} color="secondary" variant="contained">Cancel</Button>
        <Button disabled={!changed} onClick={handleSave} color="secondary" variant="contained">Save</Button> */}
      </DialogActions>

      <Dialog open={deleteOpen}>
        <DialogTitle>Delete Shopify connection?</DialogTitle>
        <DialogContent>Are you sure you want to remove <b>{name || shopify_domain}</b> from FAVES?</DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteOpen(false) }}>Cancel</Button>
          <Button color="error" onClick={handleDeleteShopifyStore} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const SquareSettings = () => {
  const { accountIntegrations, updateSquareIntegration, notify } = useContext(AuthContext);
  const settings = accountIntegrations?.square?.settings

  const [ title, setTitle ] = React.useState(settings?.hasOwnProperty('title') ? settings.title : DEFAULT_SHOPIFY_SETTINGS.title)
  const [ styleAsSku, setStyleAsSku ] = React.useState(settings?.hasOwnProperty('styleNumber_as_sku') ? settings.styleNumber_as_sku : DEFAULT_SHOPIFY_SETTINGS.styleNumber_as_sku)
  const [ brandAsVendor, setBrandAsVendor ] = React.useState(settings?.hasOwnProperty('brand_as_vendor') ? settings?.brand_as_vendor : DEFAULT_SHOPIFY_SETTINGS.brand_as_vendor)
  const [ quantity, setQuantity ] = React.useState(settings?.hasOwnProperty('set_quantity') ? settings?.set_quantity : DEFAULT_SHOPIFY_SETTINGS.set_quantity)
  const [ changed, setChanged ] = React.useState(true)

  const handleSave = () => {
    const settings = {
      ...accountIntegrations?.square,
      settings: {
        styleNumber_as_sku: styleAsSku,
        brand_as_vendor: brandAsVendor,
        set_quantity: quantity,
        title: title,
      }
    }

    updateSquareIntegration(settings, () => {
      // setChanged(false)
      notify({ message: 'Settings saved' })
    })
  }

  useEffect(() => {
    setStyleAsSku(settings?.hasOwnProperty('styleNumber_as_sku') ? settings.styleNumber_as_sku : DEFAULT_SHOPIFY_SETTINGS.styleNumber_as_sku)
    setBrandAsVendor(settings?.hasOwnProperty('brand_as_vendor') ? settings?.brand_as_vendor : DEFAULT_SHOPIFY_SETTINGS.brand_as_vendor)
    setQuantity(settings?.hasOwnProperty('set_quantity') ? settings?.set_quantity : DEFAULT_SHOPIFY_SETTINGS.set_quantity)
  }, [settings])

  return (
    <Box sx={{ 
      mt: 0,
      mb: 2,
      p: 2,
      border: '1px solid gray',
    }}>
      <IntegrationSquare/>

      {accountIntegrations?.square && (
        <>
          <ShopifyTitleGenerator onChange={setTitle} title={title} />
          <CheckListItem id="style_as_sku" onChange={setStyleAsSku} title={'Set Style Number as SKU'} value={styleAsSku} />
          <CheckListItem id="brand_as_vendor" onChange={setBrandAsVendor} title={'Set Brand as Vendor'} value={brandAsVendor} />
          <CheckListItem id="set_quantity" onChange={setQuantity} title={'Set Quantity'} value={quantity} />
          <DialogActions sx={{ p: 0 }}>
            {/* <Button disabled={!changed} onClick={handleCancel} color="secondary" variant='outlined'>Cancel</Button> */}
            <Button disabled={!changed} onClick={handleSave} color="secondary" variant={changed?'contained':'outlined'}>Save</Button>
            {/* <Button disabled={!changed} onClick={handleCancel} color="secondary" variant="contained">Cancel</Button>
            <Button disabled={!changed} onClick={handleSave} color="secondary" variant="contained">Save</Button> */}
          </DialogActions>
        </>
      )}
    </Box>
  )
}

export const Settings = () => {
  const { accountIntegrations, account, selectedStore, selectedSeason } = useContext(AuthContext);
  const [ showAddNewShopifyStore, setShowAddNewShopifyStore ] = useState(false);
  useEffect(() => { Tracking.track('Portal - Shopify settings') }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DashboardLayout>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h2" mb={"32px"}>Shopify settings</Typography>
            {!accountIntegrations?.shopify_v2 ? (
              <IntegrationShopify/>
            ) : null}

            {accountIntegrations?.shopify_v2 ? (
              <>
                <List sx={{maxWidth: 700, p: 0}}>
                  {Object.keys(accountIntegrations?.shopify_v2||{}).map(e => <ShopifySettings key={`setting-${e}`} shopify_store={e} {...accountIntegrations?.shopify_v2[e]} />)}
                </List>
              </>
            ) : null}

            {accountIntegrations && accountIntegrations?.shopify_v2 && !showAddNewShopifyStore ? (
              <Link onClick={() => { setShowAddNewShopifyStore(true) }} href="#" underline="none">+ Add new Shopify store</Link>
            ) : null}

            {showAddNewShopifyStore ? (
              <IntegrationShopify/>
            ) : null}
          </Grid>

          {account?.features?.square && (
            <Grid item xs={6}>
              <Typography variant="h2" mb={"32px"}>Square settings</Typography>
              <SquareSettings/>
            </Grid>
          )}
        </Grid>
      </DashboardLayout>
    </LocalizationProvider>
  );
};
